import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Project } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Reveal, FadeIn } from '@/components/animation/reveal'
import { ProjectGrid } from '@/components/projects/project-grid'
import {
    getProjects,
    getProjectBySlug,
    getProjectSlugs
} from '@/lib/content'

interface ProjectDetailProps {
    project: Project
    others: Project[]
}

export default function ProjectDetail({ project, others }: ProjectDetailProps) {
    return (
        <>
            <Seo
                title={project.name}
                description={project.summary}
                ogImage={project.main}
            />

            <div className='section hero'>
                <div className='container main-hero-container'>
                    <div className='w-layout-grid grid-6-columns'>
                        <div
                            id='w-node-e79b083e-68d5-d611-4424-abc638715cd3-780953d5'
                            className='perspective'
                        >
                            <FadeIn className='fade-in-slow-on-view'>
                                <h1 className='project-details-title'>{project.name}</h1>
                            </FadeIn>
                        </div>
                    </div>

                    <div
                        id='w-node-a65975c9-850a-c1f5-965e-bd1fb1131994-780953d5'
                        className='project-detail-main-image'
                    >
                        <FadeIn>
                            <img
                                loading='lazy'
                                src={project.main}
                                alt={project.name}
                                className='project-details-image'
                            />
                        </FadeIn>
                    </div>

                    <div
                        id='w-node-e739f9d7-34e9-2a32-c143-5cc6e11e3664-780953d5'
                        className='project-detail-grid'
                    >
                        <div className='content'>
                            <div className='project-details-item'>
                                <p className='project-detail-item-title'>Service</p>
                                <div className='project-service'>
                                    {project.services.map((service) => (
                                        <div key={service}>{service}</div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className='content'>
                            <div className='flext-vertical-1rem-gap'>
                                <div className='project-details-item'>
                                    <p className='project-detail-item-title'>Client</p>
                                    <div>{project.client}</div>
                                </div>
                                <div className='project-details-item'>
                                    <p className='project-detail-item-title'>Year</p>
                                    <div>{project.year}</div>
                                </div>
                            </div>
                        </div>
                        <div
                            id='w-node-aae53570-7e69-8a23-0788-b85bf3399c63-780953d5'
                            className='content'
                        >
                            <div>{project.summary}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='section'>
                <div className='container no-padding-top'>
                    <div
                        className='rich-text w-richtext'
                        dangerouslySetInnerHTML={{ __html: project.detailsHtml }}
                    />
                    {project.grid.length > 0 ? (
                        <div className='post-item-list-container'>
                            <div className='w-dyn-list'>
                                <div role='list' className='w-dyn-items'>
                                    {project.grid.map((src, index) => (
                                        <div
                                            role='listitem'
                                            className='project-image-grid-item w-dyn-item'
                                            key={index}
                                        >
                                            <img loading='lazy' alt='' src={src} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            {others.length > 0 ? (
                <div className='section'>
                    <div className='container no-max-width'>
                        <div className='heading-container'>
                            <div className='perspective'>
                                <Reveal
                                    mode='view'
                                    wrapper={false}
                                    innerClassName='perspective-load1-on-view'
                                >
                                    <h2>Other Projects</h2>
                                </Reveal>
                            </div>
                        </div>
                        <ProjectGrid projects={others} overlayWhite />
                    </div>
                </div>
            ) : null}
        </>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getProjectSlugs()
    return {
        paths: slugs.map((slug) => ({ params: { slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<ProjectDetailProps> = async ({
    params
}) => {
    const slug = params?.slug as string
    const project = await getProjectBySlug(slug)
    if (!project) return { notFound: true }

    const all = await getProjects()
    const others = all.filter((item) => item.slug !== slug).slice(0, 3)

    return { props: { project, others }, revalidate: 60 }
}
